import { useLogger } from "@agportal/logger";
import { localDate } from "@agportal/util/joda";
import { createRestClient } from "@dmvs/client";
import { strict as assert } from "assert";
import { isEmpty, isNil } from "lodash-es";

export default class VydejDatPspService {
    constructor(url) {
        assert(url, "url has to be specified");

        this.logger = useLogger();
        this.rest = createRestClient({
            baseURL: url,
        });
    }

    async vydejVlastniDataPsp(payload) {
        if (!isEmpty(payload)) {
            const { data } = await this.rest.post("/vlastni-data/pspi/pozadavek", payload);

            this.logger.debug(
                "VydejDatPspService.vydejVlastniDataPsp for {0} returns {1}",
                payload,
                data
            );

            return data;
        }
    }

    async vylistujPozadavkyVlastniDataPsp(idSubjektu, query) {
        if (!isNil(idSubjektu) && !isEmpty(query)) {
            const { data } = await this.rest.post(`/vlastni-data/pspi/pozadavky/by-subjekt/${idSubjektu}/query`, query);

            this.logger.debug(
                "VydejDatPspService.vylistujPozadavkyVlastniDataPsp for id {0} and query {1} returns {2}",
                idSubjektu,
                query,
                data
            );

            return data;
        }
    }

    async ctiPozadavekVlastniDataPsp(id) {
        if (!isNil(id)) {
            const { data } = await this.rest.get(`/vlastni-data/pspi/pozadavky/by-id-podani/${id}`);

            this.logger.debug(
                "VydejDatPspService.ctiPozadavekVlastniDataPsp for id {0} returns {1}",
                id,
                data
            );

            return data;
        }
    }

    async nactiDetailSubjektu(idSubjektu) {
        if (!isNil(idSubjektu)) {
            const { data } = await this.rest.get(`/pspi/subjekty/by-id/${idSubjektu}/detail`);

            this.logger.debug(
                "VydejDatPspService.nactiDetailSubjektu for {0} returns {1}",
                idSubjektu,
                data
            );

            return data;
        }
    }

    async vydejStavoveDatoveSady(payload) {
        if (!isEmpty(payload)) {
            const { data } = await this.rest.post("/datove-sady/pspi/stavove", payload);

            this.logger.debug(
                "VydejDatPspService.vydejStavoveDatoveSady for {0} returns {1}",
                payload,
                data
            );

            return data;
        }
    }

    async vydejZmenoveDatoveSady(payload) {
        if (!isEmpty(payload)) {
            const { data } = await this.rest.post("/datove-sady/pspi/zmenove", payload);

            this.logger.debug(
                "VydejDatPspService.vydejZmenoveDatoveSady for {0} returns {1}",
                payload,
                data
            );

            return data;
        }
    }

    async stahniSeznamDatovychSadPsp(idPodani, idSeznamu, datumVyhotoveni) {
        if (!isNil(idPodani)) {
            const { data } = await this.rest.get(`datove-sady/pspi/podani/by-uuid/${idPodani}/seznam-sad`, {
                params: {
                    idSeznamu: !isNil(idSeznamu) ? idSeznamu : undefined,
                    datumVyhotoveni: datumVyhotoveni ? localDate(datumVyhotoveni).value.toString() : undefined,
                },
            });

            this.logger.debug(
                "VydejDatPspService.stahniSeznamDatovychSadPsp for idPodani {0}, idSeznamu {1} and datumVyhotoveni {2} returns {3}",
                idPodani,
                idSeznamu,
                datumVyhotoveni,
                data,
            );

            return data;
        }

        return null;
    }

    async vyhotovDatovouSaduPspi(payload) {
        if (!isEmpty(payload)) {
            const { data } = await this.rest.post("/vyhotoveni-datovych-sad/pspi/pozadavek", payload);

            this.logger.debug(
                "VydejDatPspService.vyhotovDatovouSaduPspi for payload {0} returns {1}",
                payload,
                data
            );

            return data;
        }
    }

    async vylistujPozadavkyODataPsp(idSubjektu, query) {
        if (!isNil(idSubjektu) && !isEmpty(query)) {
            const { data } = await this.rest.post(`/pspi/subjekty/by-id/${idSubjektu}/pozadavky/query`, query);

            this.logger.debug(
                "VydejDatPspService.vylistujPozadavkyODataPsp for idSubjektu {0} and query {1} returns {2}",
                idSubjektu,
                query,
                data
            );

            return data;
        }
    }

    async ctiPozadavekAdhocDataPsp(id) {
        if (!isNil(id)) {
            const { data } = await this.rest.get(`/vyhotoveni-datovych-sad/pspi/pozadavky/by-id-podani/${id}`);

            this.logger.debug(
                "VydejDatPspService.ctiPozadavekAdhocDataPsp for id {0} returns {1}",
                id,
                data
            );

            return data;
        }
    }

    async ctiPozadavekVydejObsahuPsp(id) {
        if (!isNil(id)) {
            const { data } = await this.rest.get(`/obsah/pspi/pozadavky/by-id-podani/${id}`);

            this.logger.debug(
                "VydejDatPspService.ctiPozadavekVydejObsahuPsp for id {0} returns {1}",
                id,
                data
            );

            return data;
        }
    }

    async vydejObsahPsp(payload) {
        if (!isEmpty(payload)) {
            const { data } = await this.rest.post("/obsah/pspi/pozadavek", payload);

            this.logger.debug(
                "VydejDatPspService.vydejObsahPsp for payload {0} returns {1}",
                payload,
                data
            );

            return data;
        }
    }

    async nactiUlohuDtmk(idHlavicky) {
        if (!isNil(idHlavicky)) {
            const { data } = await this.rest.get(`/vyhotoveni-datovych-sad/pspi/ulohy-isdtmk/schvaleni-vydeje/${idHlavicky}`);

            this.logger.debug(
                "VydejDatPspService.nactiUlohuDtmk for idHlavicky {0} returns {1}",
                idHlavicky,
                data
            );

            return data;
        }
    }

    async vyridUlohuDtmk(idHlavicky, payload) {
        if (!isNil(idHlavicky) && !isEmpty(payload)) {
            const { data } = await this.rest.post(`/vyhotoveni-datovych-sad/pspi/ulohy-isdtmk/schvaleni-vydeje/${idHlavicky}/vyjadreni`, payload);

            this.logger.debug(
                "VydejDatPspService.vyridUlohuDtmk for idHlavicky {0} and payload {1} returns {2}",
                idHlavicky,
                payload,
                data
            );

            return data;
        }
    }

    async stahniPrilohuUlohyDtmk(idHlavicky, idSouboru) {
        if (!isNil(idHlavicky) && !isNil(idSouboru)) {
            const { data } = await this.rest.get(`/vyhotoveni-datovych-sad/pspi/ulohy-isdtmk/schvaleni-vydeje/${idHlavicky}/priloha-zadost/${idSouboru}`, {
                responseType: "blob",
            });

            this.logger.debug("VydejDatPspService.stahniPrilohuUlohyDtmk for idHlavicky {0} and idSouboru {1} returns {2}", idHlavicky, idSouboru, data);

            return new Blob([data], {
                type: data.type,
            });
        }

        return null;
    }

    async stahniPrilohuVyhotoveniDatPspDleZadosti(idPodani, idSouboru) {
        if (!isNil(idPodani) && !isNil(idSouboru)) {
            const { data } = await this.rest.get(`/vyhotoveni-datovych-sad/pspi/pozadavky/by-id-podani/${idPodani}/priloha-zadost/${idSouboru}`, {
                responseType: "blob",
            });

            this.logger.debug("VydejDatPspService.stahniPrilohuVyhotoveniDatPspDleZadosti for idPodani {0} and idSouboru {1} returns {2}", idPodani, idSouboru, data);

            return new Blob([data], {
                type: data.type,
            });
        }

        return null;
    }
}
